import {DataStatus, TabProps, Tabs} from 'platform/components';

import {AftersalesPaymentMethod, useGetCheckoutPaymentsV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {queryParams} from '@dms/shared';

import {RequiredTestIdProps, buildArray, suffixTestId, useQueryState} from 'shared/web';

import {AftersalesCheckoutBillingInformation} from '../AftersalesCheckoutBillingInformation/AftersalesCheckoutBillingInformation';
import {AftersalesCheckoutPayment} from '../AftersalesCheckoutPayment/AftersalesCheckoutPayment';

interface CheckoutProps extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  recordId?: string;
  resourceId?: EntityResourceIds;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  isBillingInformationEditingDisabled?: boolean;
  serviceCaseId?: string;
}

export function AftersalesCheckout(props: CheckoutProps) {
  const [checkoutTabId, setCheckoutTabId] = useQueryState(queryParams.AFTERSALES_CHECKOUT_TAB);

  const {data, isLoading, isError} = useGetCheckoutPaymentsV2Query(
    {checkoutId: props.checkoutId},
    {refetchOnMountOrArgChange: true}
  );

  const relevantPayments = data?.filter(
    ({discriminator}) => discriminator === 'BALANCE' || discriminator === 'DEPOSIT'
  );
  const areAllPaymentsPaid =
    relevantPayments && relevantPayments.length > 0
      ? relevantPayments.every(({paymentState}) => paymentState === 'PAID')
      : false;

  const tabs = buildArray<TabProps>()
    .add({
      id: 'billingInformation',
      content: (
        <AftersalesCheckoutBillingInformation
          checkoutId={props.checkoutId}
          recordId={props.recordId}
          resourceId={props.resourceId}
          isEditingDisabled={props.isEditingDisabled || props.isBillingInformationEditingDisabled}
          data-testid={props['data-testid']}
        />
      ),
      title: i18n.t('entity.checkout.labels.billingInformation'),
      icon: 'success',
    })
    .add({
      id: 'payment',
      content: (
        <AftersalesCheckoutPayment
          checkoutId={props.checkoutId}
          serviceCaseId={props.serviceCaseId}
          serviceOrderId={props.recordId}
          data-testid={props['data-testid']}
          defaultPaymentType={props.defaultPaymentType}
          isEditingDisabled={props.isEditingDisabled}
          isCompletingDisabled={props.isCompletingDisabled}
        />
      ),
      title: i18n.t('entity.checkout.labels.payment'),
      icon: areAllPaymentsPaid ? 'success' : 'pending',
    });

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Tabs
        onChange={setCheckoutTabId}
        activeTabId={checkoutTabId}
        tabs={tabs}
        variant="condensed"
        data-testid={suffixTestId('checkoutTabs', props)}
        isLazy
        lazyBehavior="unmount"
      />
    </DataStatus>
  );
}
